/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CalendarDays, MapPin, Users, FileText, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const frDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "");

export const TypeBadge = ({ type, size = "sm" }) => {
  const self = type === "selfservice";
  const cls = size === "lg" ? "text-sm px-3 py-1.5" : "text-[11px] px-2 py-0.5";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-bold uppercase tracking-wide ${cls} ${self ? "bg-emerald-500 text-white" : "bg-[#003087] text-white"}`} data-testid={`type-badge-${type}`}>
      {self ? "⚡ Self-service" : "🏫 Présentiel / Mixte"}
    </span>
  );
};

export const useSessions = (formationCode) => {
  const [sessions, setSessions] = useState([]);
  const load = useCallback(() => {
    axios.get(`${API}/sessions`, { params: formationCode ? { formation_code: formationCode } : {} }).then((r) => setSessions(r.data)).catch(() => setSessions([]));
  }, [formationCode]);
  useEffect(() => { load(); }, [load]);
  return sessions;
};

export const SessionRow = ({ s, onChoose, chosen, showFormation }) => (
  <div className={`flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-xl border ${chosen ? "border-[#F07B1D] bg-[#FFF8F1]" : "border-gray-200 bg-white"}`} data-testid={`session-${s.id}`}>
    <div className="flex-1 min-w-0">
      {showFormation && <p className="text-xs font-bold text-[#F07B1D] uppercase tracking-wide">{s.formation_code} — {s.formation_title}</p>}
      <p className="font-semibold text-[#003087] text-sm flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> Du {frDate(s.date_debut)} au {frDate(s.date_fin)} <span className="text-gray-500 font-normal">· {s.horaires}</span></p>
      <p className="text-xs text-gray-600 flex items-center gap-1.5 mt-0.5"><MapPin className="h-3.5 w-3.5" /> {s.lieu}</p>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <span className={`text-xs font-semibold flex items-center gap-1 ${s.statut === "complete" ? "text-red-600" : "text-emerald-700"}`}><Users className="h-3.5 w-3.5" /> {s.statut === "complete" ? "Complet" : `${s.places_restantes} place${s.places_restantes > 1 ? "s" : ""} restante${s.places_restantes > 1 ? "s" : ""}`}</span>
      {onChoose && s.statut !== "complete" && <Button size="sm" variant={chosen ? "default" : "outline"} className={chosen ? "bg-[#F07B1D] text-white" : "border-[#003087] text-[#003087]"} onClick={() => onChoose(s)} data-testid={`choose-session-${s.id}`}>{chosen ? "Sélectionnée" : "Choisir"}</Button>}
    </div>
  </div>
);

const EMPTY = { type_client: "particulier", prenom: "", nom: "", email: "", telephone: "", entreprise: "", siret: "", adresse: "", opco: "", message: "" };

export const DevisForm = ({ formation, sessions }) => {
  const [form, setForm] = useState(EMPTY);
  const [session, setSession] = useState(null);
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pro = form.type_client !== "particulier";

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Merci d'accepter la politique de confidentialité");
    setSending(true);
    try {
      const res = await axios.post(`${API}/devis`, { ...form, formation_code: formation.code, session_id: session?.id || null, consent_rgpd: consent });
      setDone(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border-2 border-emerald-500 bg-emerald-50 p-6 text-center" data-testid="devis-success">
        <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
        <h3 className="font-heading text-xl font-bold text-[#003087] mb-2">Devis {done.numero_devis} envoyé !</h3>
        <p className="text-sm text-gray-700">Votre devis PDF vient de vous être adressé par e-mail, avec un lien pour le signer électroniquement puis signer votre {pro ? "convention" : "contrat"} de formation. Nous vous recontactons sous 24 h ouvrées.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="devis-form" id="devis">
      <div className="grid grid-cols-3 gap-2" data-testid="devis-type-client">
        {[["particulier", "Particulier"], ["entreprise", "Entreprise"], ["opco", "Entreprise + OPCO"]].map(([v, lab]) => (
          <button key={v} type="button" onClick={() => set("type_client", v)} className={`py-2 rounded-lg border-2 font-semibold text-xs sm:text-sm transition-colors ${form.type_client === v ? "border-[#003087] bg-[#003087] text-white" : "border-gray-200 text-gray-600 hover:border-[#003087]"}`} data-testid={`devis-type-${v}`}>{lab}</button>
        ))}
      </div>
      {sessions.length > 0 && (
        <div>
          <Label className="text-sm font-semibold text-[#003087]">Session souhaitée (facultatif)</Label>
          <div className="space-y-2 mt-1">{sessions.map((s) => <SessionRow key={s.id} s={s} onChoose={(x) => setSession(session?.id === x.id ? null : x)} chosen={session?.id === s.id} />)}</div>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label className="text-sm font-semibold text-[#003087]">Prénom *</Label><Input required value={form.prenom} onChange={(e) => set("prenom", e.target.value)} data-testid="devis-prenom" /></div>
        <div><Label className="text-sm font-semibold text-[#003087]">Nom *</Label><Input required value={form.nom} onChange={(e) => set("nom", e.target.value)} data-testid="devis-nom" /></div>
        <div><Label className="text-sm font-semibold text-[#003087]">E-mail *</Label><Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="devis-email" /></div>
        <div><Label className="text-sm font-semibold text-[#003087]">Téléphone</Label><Input value={form.telephone} onChange={(e) => set("telephone", e.target.value)} data-testid="devis-telephone" /></div>
        {pro && (
          <>
            <div><Label className="text-sm font-semibold text-[#003087]">Entreprise *</Label><Input required value={form.entreprise} onChange={(e) => set("entreprise", e.target.value)} data-testid="devis-entreprise" /></div>
            <div><Label className="text-sm font-semibold text-[#003087]">SIRET</Label><Input value={form.siret} onChange={(e) => set("siret", e.target.value)} data-testid="devis-siret" /></div>
          </>
        )}
        {form.type_client === "opco" && <div className="sm:col-span-2"><Label className="text-sm font-semibold text-[#003087]">Votre OPCO (ex. AKTO, OPCO EP, Atlas…)</Label><Input value={form.opco} onChange={(e) => set("opco", e.target.value)} data-testid="devis-opco" /></div>}
        <div className="sm:col-span-2"><Label className="text-sm font-semibold text-[#003087]">Adresse (pour le devis)</Label><Input value={form.adresse} onChange={(e) => set("adresse", e.target.value)} data-testid="devis-adresse" /></div>
        <div className="sm:col-span-2"><Label className="text-sm font-semibold text-[#003087]">Votre besoin / contexte</Label><Input value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="devis-message" /></div>
      </div>
      <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
        <input type="checkbox" className="mt-0.5 accent-[#F07B1D]" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="devis-rgpd" />
        <span>J'accepte que mes données soient traitées par MGB School pour établir mon devis et me recontacter (<Link to="/confidentialite" className="underline text-[#F07B1D]">politique de confidentialité</Link>). *</span>
      </label>
      <Button type="submit" disabled={sending} className="w-full bg-[#F07B1D] hover:bg-[#D96A0F] text-white font-bold py-5" data-testid="devis-submit">
        <FileText className="h-4 w-4 mr-2" /> {sending ? "Génération du devis…" : "Recevoir mon devis par e-mail"}
      </Button>
      <p className="text-[11px] text-gray-500 text-center">Devis numéroté généré instantanément · {formation.price} € net de taxe · Convention (entreprise) ou contrat (particulier) avec délai de rétractation de 10 jours.</p>
    </form>
  );
};
